import {
    forwardRef,
    useEffect,
    Fragment,
    type Ref,
    useRef
} from "react";
import {
    type NativeSyntheticEvent,
    type NativeScrollEvent,
    type ScrollViewProps,
    ScrollView,
    View
} from "react-native";
import type IPageContainerProps from "./type";
import {
    type IPageContainerRef
} from "./type";
import stylesheet, {
    getIsPageContainerSized,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    type RefForwardingComponent
} from "../../types";
import {
    SafeAreaView
} from "react-native-safe-area-context";
import {
    warnOnce
} from "../../utils/development";

const PageContainer: RefForwardingComponent<IPageContainerRef, IPageContainerProps> = ({
    backgroundColor = "default" as keyof NCoreUIKit.ContainerContentColors,
    isWrapSafeAreaContext: isWrapSafeAreaContextProp,
    isWorkWithContentMaxWidth = false,
    isWorkWithHeaderSpace = true,
    safeAreaViewBackgroundColor,
    isCustomPadding = false,
    isWrapSafeareaContext,
    isScrollable = false,
    onScrollEdgeChange,
    safeAreaViewStyle,
    scrollViewProps,
    scrollViewStyle,
    renderOverlays,
    customTheme,
    children,
    style,
    ...props
}, ref) => {
    const {
        shapeConfigs,
        durations,
        configs,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const isScrolledRef = useRef(false);

    const isWrapWithSafeAreaView = isWrapSafeAreaContextProp ?? isWrapSafeareaContext ?? false;

    const isScrollViewPropsIgnored = !isScrollable && scrollViewProps !== undefined;

    const isScrollViewStyleIgnored = !isScrollable && scrollViewStyle !== undefined;

    const isStyleIgnored = isScrollable && style !== undefined;

    const isScrollViewSized = getIsPageContainerSized({
        style: [
            scrollViewProps?.style,
            scrollViewStyle
        ]
    });

    const isSafeAreaViewSized = getIsPageContainerSized({
        style: safeAreaViewStyle
    });

    const isViewSized = getIsPageContainerSized({
        style
    });

    const {
        contentColumn: contentColumnDynamicStyle,
        safeAreaView: safeAreaViewDynamicStyle,
        container: containerDynamicStyle,
        content: contentDynamicStyle
    } = useStyles({
        safeAreaViewBackgroundColor,
        isWorkWithHeaderSpace,
        backgroundColor,
        isCustomPadding,
        shapeConfigs,
        configs,
        colors,
        spaces
    });

    useEffect(() => {
        if(isWrapSafeareaContext === undefined) {
            return;
        }

        warnOnce({
            message: "PageContainer: \"isWrapSafeareaContext\" is deprecated and will be removed in the next major version. Use \"isWrapSafeAreaContext\" instead."
        });
    }, [
        isWrapSafeareaContext
    ]);

    useEffect(() => {
        if(!isStyleIgnored) {
            return;
        }

        warnOnce({
            message: "PageContainer: \"style\" is ignored when \"isScrollable\" is true. Use \"scrollViewStyle\" instead."
        });
    }, [
        isStyleIgnored
    ]);

    useEffect(() => {
        if(!isScrollViewStyleIgnored) {
            return;
        }

        warnOnce({
            message: "PageContainer: \"scrollViewStyle\" is ignored when \"isScrollable\" is false. Use \"style\" instead."
        });
    }, [
        isScrollViewStyleIgnored
    ]);

    useEffect(() => {
        if(!isScrollViewPropsIgnored) {
            return;
        }

        warnOnce({
            message: "PageContainer: \"scrollViewProps\" is ignored when \"isScrollable\" is false."
        });
    }, [
        isScrollViewPropsIgnored
    ]);

    const updateScrollEdge = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        if(!onScrollEdgeChange) {
            return;
        }

        const isScrolled = event.nativeEvent.contentOffset.y > 0;

        if(isScrolled === isScrolledRef.current) {
            return;
        }

        isScrolledRef.current = isScrolled;

        onScrollEdgeChange(isScrolled);
    };

    const scrollEdgeProps: ScrollViewProps = onScrollEdgeChange ? {
        scrollEventThrottle: scrollViewProps?.scrollEventThrottle ?? durations.short,
        onMomentumScrollEnd: (event) => {
            scrollViewProps?.onMomentumScrollEnd?.(event);

            updateScrollEdge(event);
        },
        onScrollEndDrag: (event) => {
            scrollViewProps?.onScrollEndDrag?.(event);

            updateScrollEdge(event);
        },
        onScroll: (event) => {
            scrollViewProps?.onScroll?.(event);

            updateScrollEdge(event);
        }
    } : {};

    const renderContent = () => {
        if(!isWorkWithContentMaxWidth) {
            return children;
        }

        return <View
            style={[
                stylesheet.contentColumn,
                contentColumnDynamicStyle
            ]}
        >
            {children}
        </View>;
    };

    const renderScrollView = () => {
        return <ScrollView
            keyboardShouldPersistTaps="handled"
            ref={ref as Ref<ScrollView>}
            {...props}
            {...scrollViewProps}
            {...scrollEdgeProps}
            style={[
                isScrollViewSized ? undefined : stylesheet.container,
                containerDynamicStyle,
                scrollViewProps?.style,
                scrollViewStyle
            ]}
            contentContainerStyle={[
                contentDynamicStyle,
                scrollViewProps?.contentContainerStyle
            ]}
        >
            {renderContent()}
        </ScrollView>;
    };

    const renderView = () => {
        return <View
            ref={ref as Ref<View>}
            {...props}
            style={[
                isViewSized ? undefined : stylesheet.container,
                containerDynamicStyle,
                contentDynamicStyle,
                style
            ]}
        >
            {renderContent()}
        </View>;
    };

    const renderWithSafeAreaView = () => {
        return <SafeAreaView
            style={[
                safeAreaViewStyle,
                isSafeAreaViewSized ? undefined : stylesheet.safeAreaViewContainer,
                safeAreaViewDynamicStyle
            ]}
        >
            {isScrollable ? renderScrollView() : renderView()}
            {renderOverlays ? renderOverlays() : null}
        </SafeAreaView>;
    };

    const renderSafeAreaContext = () => {
        return isWrapWithSafeAreaView ? renderWithSafeAreaView() : <Fragment>
            {isScrollable ? renderScrollView() : renderView()}
            {renderOverlays ? renderOverlays() : null}
        </Fragment>;
    };

    return renderSafeAreaContext();
};
export default forwardRef(PageContainer);
